import React from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react'
import type { IconComponent } from '@/lib/icons'
import { Badge } from '../ui/badge'
import { cn } from '@/lib/utils'
import { Button } from '../ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu'
import { useAuth } from '@/lib/AuthContext'
import { PRESENCE_SLOT, ScreenPresence } from '@/lib/realtime/ScreenPresence'
import { useNativeHidden } from '@/lib/navigation'

interface PageHeaderMenuItem {
  label: string
  icon: IconComponent
  onClick: () => void
  /** A line above this item, to split groups (content types, then other actions). */
  separatorBefore?: boolean
}

/** One short fact under an item's title: period, deadline, reminder (PADROES-UI section 4, item header). */
export interface PageHeaderMetaItem {
  icon?: IconComponent
  label: React.ReactNode
  /** The full text when the label is abbreviated. */
  title?: string
  testId?: string
}

interface PageHeaderProps {
  icon: IconComponent
  label: string
  description?: string
  isLoading?: boolean
  secondaryAction?: {
    label: string
    icon: IconComponent
    onClick: () => void
  }
  /** A management action (owner/admin), shown before the secondary one. The caller decides who sees it. */
  manageAction?: {
    label: string
    icon: IconComponent
    onClick: () => void
  }
  action?: {
    label: string
    icon: IconComponent
    onClick: () => void
    /** More actions behind a chevron next to the main one (split button). Empty or absent: a plain button. */
    menu?: PageHeaderMenuItem[]
  }
  /** Screen-specific controls (a channel's members, mute and search), placed first in the actions area. */
  extraActions?: React.ReactNode
  /**
   * Inside an item (a cycle, a form, a call): the space it belongs to, as a small link above
   * the title. It replaces a separate "Back" row. Omit it in a one-item space, where the item
   * is the space.
   */
  breadcrumb?: { label: string; onClick: () => void; section?: { label: string; onClick: () => void } | null }
  /** The item's state, as a pill at the start of the meta line. */
  status?: { label: string; variant?: React.ComponentProps<typeof Badge>['variant']; testId?: string }
  /** Short facts under the title (period, deadline), after the status. */
  meta?: PageHeaderMetaItem[]
}

export const PageHeader = ({ icon: Icon, label, description, isLoading = false, action, secondaryAction, manageAction, extraActions, breadcrumb, status, meta }: PageHeaderProps) => {
  const { isAuthenticated } = useAuth()
  /* F-191 phase 5: the community hides "who is on this screen" (§4.5.1). */
  const presenceHidden = useNativeHidden('presence')
  const { t } = useTranslation('common')

  if (isLoading) {
    return (
      <div className="bg-white border-b px-4 py-5 sm:px-6 sm:py-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-start gap-4 flex-1">
              <div className="w-12 h-12 rounded-xl bg-gray-100 flex-shrink-0 flex items-center justify-center">
                <div className="w-6 h-6 bg-gray-300 rounded animate-pulse" />
              </div>
              <div className="space-y-3 flex-1 min-w-0">
                <div className="h-8 bg-gray-200 rounded w-full max-w-md animate-pulse" />
                <div className="h-5 bg-gray-200 rounded w-full max-w-2xl animate-pulse" />
              </div>
            </div>
            <div className="flex items-center gap-2 mt-4 sm:mt-0">
              <div className="h-10 w-32 bg-gray-200 rounded-lg animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    )
  }

  /* Below 640 px the actions stay on the title line (PADROES-UI §7): every action shows only its icon, with
     the label in the tooltip and aria-label (the Channel's mute and search already did this). The main action
     keeps its filled color, so it still reads as the main one; a label next to the title broke it mid-word. */
  const ghostAction = 'h-10 w-10 gap-2 bg-primary-50/50 px-0 text-primary-700 hover:bg-primary-100 hover:text-primary-800 sm:w-auto sm:px-8'
  const iconLabel = 'hidden sm:inline'
  const textBlock = Boolean(description || status || meta?.length)
  return (
    <div className="bg-white border-b px-4 py-5 sm:px-6 sm:py-6">
      <div className="max-w-7xl mx-auto">
        {/* One grid: icon · title · actions on the first row; description and meta under the title
            (on a phone they take the whole width, so the title line keeps room for the actions). */}
        <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 sm:gap-x-4" data-testid="page-header">
          <div className={cn('col-start-1 row-start-1 flex h-10 w-10 flex-shrink-0 items-center justify-center self-start rounded-xl bg-primary-50 sm:h-12 sm:w-12', textBlock && 'sm:row-span-2')}>
            <Icon className="h-5 w-5 min-w-[1.25rem] text-primary-600 sm:h-6 sm:w-6 sm:min-w-[1.5rem]" />
          </div>
          <div className="col-start-2 row-start-1 min-w-0 self-center">
            {breadcrumb && (
              <div className="mb-0.5 flex min-w-0 max-w-full items-center text-sm font-medium text-gray-500">
                {/* "Section ›" before the space, from a wider screen up (on a phone the space alone is the way back). */}
                {breadcrumb.section && (
                  <span className="hidden min-w-0 items-center gap-0.5 sm:inline-flex">
                    <button type="button" onClick={breadcrumb.section.onClick} className="truncate hover:text-primary-700" data-testid="page-header-breadcrumb-section">
                      {breadcrumb.section.label}
                    </button>
                    <ChevronRight className="mr-0.5 h-4 w-4 flex-shrink-0" />
                  </span>
                )}
                <button type="button" onClick={breadcrumb.onClick} data-testid="page-header-breadcrumb"
                  className="inline-flex min-h-9 min-w-0 max-w-full items-center gap-0.5 hover:text-primary-700 sm:min-h-0">
                  <ChevronLeft className="h-4 w-4 flex-shrink-0 sm:hidden" />
                  <span className="truncate">{breadcrumb.label}</span>
                  <ChevronRight className="hidden h-4 w-4 flex-shrink-0 sm:block" />
                </button>
              </div>
            )}
            <h1 className="break-words text-xl font-bold text-gray-900 sm:text-2xl">{label}</h1>
          </div>
          {textBlock && (
            <div className="col-span-3 col-start-1 row-start-2 min-w-0 sm:col-span-1 sm:col-start-2">
              {description && (
                <p className="mt-2 text-gray-600 sm:mt-1">{description}</p>
              )}
              {(status || Boolean(meta?.length)) && (
                <div className={cn('flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-gray-600', description ? 'mt-2' : 'mt-2 sm:mt-1.5')} data-testid="page-header-meta">
                  {status && <Badge variant={status.variant} className="flex-shrink-0"><span data-testid={status.testId}>{status.label}</span></Badge>}
                  {meta?.map((item, index) => (
                    <span key={index} className="inline-flex min-w-0 items-center gap-1.5" title={item.title} data-testid={item.testId}>
                      {item.icon && <item.icon className="h-4 w-4 flex-shrink-0 text-gray-400" />}
                      <span className="min-w-0">{item.label}</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className={cn('col-start-3 row-start-1 flex items-center justify-end gap-2 self-center sm:flex-wrap', textBlock && 'sm:row-span-2')} data-testid="page-header-actions">
            {/* Who else is on this screen (nothing when nobody is). */}
            {isAuthenticated && !presenceHidden && <ScreenPresence className="mr-1" priority={PRESENCE_SLOT.header} />}
            {extraActions}
            {isAuthenticated && manageAction && (
              <Button size="lg" variant="ghost" className={ghostAction} onClick={manageAction.onClick} aria-label={manageAction.label} title={manageAction.label}>
                <manageAction.icon className="w-4 h-4" />
                <span className={iconLabel}>{manageAction.label}</span>
              </Button>
            )}
            {isAuthenticated && secondaryAction && (
              <Button size="lg" variant="ghost" className={ghostAction} onClick={secondaryAction.onClick} aria-label={secondaryAction.label} title={secondaryAction.label}>
                <secondaryAction.icon className="w-4 h-4" />
                <span className={iconLabel}>{secondaryAction.label}</span>
              </Button>
            )}
            {isAuthenticated && action && (action.menu?.length ? (
              <div className="inline-flex" data-testid="page-header-split-action">
                <Button size="lg" variant="create" className="w-10 gap-2 rounded-r-none px-0 sm:w-auto sm:px-8" onClick={action.onClick}
                  aria-label={action.label} title={action.label}>
                  <action.icon className="w-4 h-4" />
                  <span className={iconLabel}>{action.label}</span>
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="lg" variant="create" className="w-9 rounded-l-none border-l border-primary-200 px-0 sm:w-auto sm:px-3" aria-label={t('moreActions')} data-testid="page-header-split-toggle">
                      <ChevronDown className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {action.menu.map((item, index) => (
                      <React.Fragment key={item.label}>
                        {item.separatorBefore && index > 0 && <DropdownMenuSeparator />}
                        <DropdownMenuItem onClick={item.onClick} className="gap-2">
                          <item.icon className="w-4 h-4" />
                          {item.label}
                        </DropdownMenuItem>
                      </React.Fragment>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ) : (
              <Button size="lg" variant="create" className="w-10 gap-2 px-0 sm:w-auto sm:px-8" onClick={action.onClick}
                aria-label={action.label} title={action.label}>
                <action.icon className="w-4 h-4" />
                <span className={iconLabel}>{action.label}</span>
              </Button>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}